import React, { createContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosConfig';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCompany, setActiveCompany] = useState(null);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/me');
      setCurrentUser(response.data);
    } catch (error) {
      // Si la récupération échoue (token invalide/expiré), on déconnecte
      // On ne déconnecte pas ici pour éviter une boucle, on laisse le useEffect principal gérer
      console.error("Failed to fetch current user, likely invalid token.", error);
      throw error; // Propage l'erreur pour que le .catch() du useEffect puisse la gérer
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('activeCompanyId');
    delete axiosInstance.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setActiveCompany(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser().catch(logout); // Déconnecte si fetchCurrentUser échoue
    } else {
      setLoading(false);
    }
  }, [fetchCurrentUser, logout]);

  useEffect(() => {
    // On ne travaille qu'avec les entreprises où l'utilisateur est actif
    const activeCompanies = currentUser?.Companies?.filter(c => c.CompanyUser.status === 'active') || [];

    if (currentUser && !activeCompany && activeCompanies.length > 0) {
      // Si l'utilisateur est chargé mais pas l'entreprise, on la définit.
      const storedCompanyId = localStorage.getItem('activeCompanyId');
      const companyToSet = activeCompanies.find(c => c.id.toString() === storedCompanyId) || activeCompanies[0];
      setActiveCompany(companyToSet);
    } else if (currentUser && activeCompany) {
      // Si tout est chargé, on peut arrêter le chargement.
      localStorage.setItem('activeCompanyId', activeCompany.id);
      setLoading(false);
    } else if (currentUser && activeCompanies.length === 0) {
      // Utilisateur sans entreprise, on peut arrêter le chargement.
      setLoading(false);
    }
  }, [currentUser, activeCompany]);

  // Effet pour rafraîchir périodiquement les données de l'utilisateur
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        fetchCurrentUser();
      }
    }, 1000 * 60 * 60); // 1 heure

    // Nettoyage de l'intervalle lorsque le composant est démonté
    return () => clearInterval(refreshInterval);
  }, [fetchCurrentUser]);

  const login = async (email, password) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    localStorage.setItem('token', response.data.token);
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    await fetchCurrentUser();
  };

  const recovery = async (email) => {
    const response = await axiosInstance.post('/auth/recovery', { email });
    console.log(response.data);
  };

  const resetPassword = async (token, password) => {
    const response = await axiosInstance.post('/auth/reset', { token, password });
    console.log(response.data);
  };

  const signup = async (email, password, firstName, lastName, role) => {
    const response = await axiosInstance.post('/auth/signup', { email, password, firstName, lastName, role });
    localStorage.setItem('token', response.data.token);
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    await fetchCurrentUser();
  };

  return (
    <AuthContext.Provider value={{ currentUser, setCurrentUser, activeCompany, setActiveCompany, login, signup, recovery, resetPassword, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;